import { Injectable } from '@angular/core';
import { addDoc, collectionData, deleteDoc, doc, docData, Firestore, query, setDoc, where, CollectionReference, DocumentData, collection, getDocs, getDoc, updateDoc, orderBy, limit, startAfter, documentId } from '@angular/fire/firestore';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { TabsService } from '../../tabs.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { CreateOrganisationHierarchyService } from '../create-organisation-hierarchy/create-organisation-hierarchy.service';
import { SharedService } from 'src/app/shared/shared.service';
import { environment } from 'src/environments/environment';
import { PasswordManagementService } from 'src/app/shared/services/password-management.service';
const XLSX = require("xlsx");
declare var Typesense: any;


@Injectable({
  providedIn: 'root'
})
export class UsersService {
	bulkUploadDetails$ = new BehaviorSubject<string>('File Uploaded. Waiting to start import...');
	private usersCollection: CollectionReference<DocumentData>;
	private rolesCollection: CollectionReference<DocumentData>;
	private groupsCollection: CollectionReference<DocumentData>;
	private tempUsersCollection: CollectionReference<DocumentData>;
	private progressCollection: CollectionReference<DocumentData>;
	emailRegex = /^\S+@\S+\.\S+$/;
	typesenseClient: any;
	typesenseCred = environment.typesense;
	// "bwi-ignite-users-prod" is the collection the backend actually syncs to (see
	// functions/process/search.js). "ignite-users-prod" is a legacy index that no longer
	// receives creates, updates or deletes - keep it only as a fallback, never first.
	usersSearchCollections = ['bwi-ignite-users-prod', 'ignite-users-prod'];

	constructor(
		private firestore:Firestore,
		private tabsService:TabsService,
			private spinner: NgxSpinnerService,
			private sharedService: SharedService,
			private createOrganisationHierarchyService: CreateOrganisationHierarchyService,
			private passwordManagementService: PasswordManagementService
		) {
		this.usersCollection = collection(this.firestore, "users");
		this.rolesCollection = collection(this.firestore, "roles");
		this.groupsCollection = collection(this.firestore, "groups");
		this.tempUsersCollection = collection(this.firestore, "tempUsers");
		this.progressCollection = collection(this.firestore, "progress");
	}

	async addEditUser(data: any, id: string) {
		data.updatedAt = new Date();
		// console.log(data);

			if (!id) {
				await addDoc(collection(this.firestore, "tempUsers"), data);
			} else {
				const password = data.password ? String(data.password) : '';
				delete data.password;
				const ref = doc(this.firestore, `users/${id}`);
				await setDoc(ref, data, { merge: true });

				if (password) {
					await this.passwordManagementService.adminSetPassword(id, password, false);
				}
			}
	}

	getUsers(organisationId:any) {
		const refq = query(this.usersCollection ,where('organisationId', '==' ,organisationId));
		return collectionData(refq, { idField: 'id' }) as Observable<any>;
	}

	async deleteUser(id:string) {
		if (!id) {
			console.log('Unable to delete user: missing id');
			return { status: 'failed' };
		}

		try {
			await this.deleteUserFromProgressCollection(id);
			const del = doc(this.firestore, `users/${id}`);
			await deleteDoc(del);
			return { status: 'success' };
		} catch(err) {
			console.log('Unable to delete user', err);
			return { status: 'failed' };
		}
	}

	async deleteUserFromProgressCollection(userId: string): Promise<void> {
		const q = query(collection(this.firestore, 'progress'), where('userId', '==', userId));
		const querySnapshot = await getDocs(q);
		// NOTE: this local must not be named `deleteDoc` - that shadows the imported
		// `deleteDoc` for the whole function scope and makes the calls below throw a
		// temporal-dead-zone ReferenceError, silently orphaning every progress record.
		const deletions = querySnapshot.docs.map((progressDoc) => deleteDoc(progressDoc.ref));
		await Promise.all(deletions);
	}

	async addEditGroup(data: any, id:string) {
		let collectionRef = 'groups';
		// console.log('id',id);
		if (!id) {
			id = doc(collection(this.firestore, collectionRef)).id;
		}

		data.id = id;

		// if (data.url.includes('data:image/jpeg;base64,') || data.url.includes('data:image/jpg;base64,') || data.url.includes('data:image/png;base64,')) {
		//   data.url = await this.getUrlForUploadedImage(data.url, `brands/${data.id}/images/image.png`)
		// }

		collectionRef = `groups/${id}`;
		const ref = doc(this.firestore, collectionRef);
		return setDoc(ref, data);
	}

	getGroups(organisationId:any){
		const ref = collection(this.firestore, 'groups');
		const refq = query(ref,where('organisationId','==',organisationId))
		return collectionData(refq, {idField: 'id'}) as Observable<any>;
	}

	deleteGroup(id:string) {
		return new Promise<any>((resolve, reject) => {
			try {
				const del = doc(this.firestore, `groups/${id}`);
				deleteDoc(del);
				resolve(true);
			} catch(err){
				resolve(false);
			}
		});
	}

	async addEditRole(data: any, id:string) {
		let collectionRef = 'roles';
		// console.log('id',id)
		if(!id) {
			id = doc(collection(this.firestore, collectionRef)).id;
		}
		data.id = id;
		collectionRef = `roles/${id}`;
		const ref = doc(this.firestore, collectionRef);
		return setDoc(ref, data);
	}

	getRoles(organisationId:any){
		const ref = collection(this.firestore, 'roles');
		const refq = query(ref, where('organisationId', '==', organisationId));
		return collectionData(refq, { idField: 'id' }) as Observable<any>;
	}

	deleteRole(id:string) {
		return new Promise<any>((resolve, reject) => {
			try{
				const del = doc(this.firestore, `roles/${id}`);
				deleteDoc(del);
				resolve(true)
			}catch(err){
				resolve(false);
			}
		});
	}

	async getOrganizationLevels() {
		// const orgId = this.tabsService.organisationId$.getValue();
		// console.log(orgId);
		// const obs: any = docData(doc(this.firestore, `organisations/${orgId}`));
		// const orgData = await this.sharedService.convertObsToValue(obs);
		// return orgData && orgData.hierarchy ? orgData.hierarchy : [];
		await this.spinner.show("blockLoader");
		const allTemplates = await this.createOrganisationHierarchyService.getOrgTemplate();
		await this.spinner.hide("blockLoader");
		const template = allTemplates[0];
		if (template && template.hierarchies.length >= 1) {
			return template.hierarchies;
		}

		return [];
	}

	async processExcelFile(fileBuffer: any, hierarchies: any, organisationHierarchy: any, organisationId: string, orgCode: string) {
		this.bulkUploadDetails$.next("File is being imported...");
		var workbook = XLSX.read(fileBuffer, {type: 'array'});
		var result: any = {};
		var sheetName = workbook.SheetNames[0];
		let roa = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 0 });
		if (roa.length) {
			result[sheetName] = roa;
		}

		// console.log(result);
		if (!result.Sheet1) {
			return {
				status: 0,
				msg: "Please make sure that your sheet is named Sheet1."
			}
		}
		let fileData = result.Sheet1;
		let areRowsValid = this.checkIfExcelIsValid(fileData);

		if (areRowsValid.isValid) {
			let unsuccessfulCount = 0, addedCount = 0, editedCount = 0;
			const orgRoles = await this.getOrgRoles(organisationId);

			for (let user of fileData) {
				user = this.getTrimmedUser(user);
				// console.log(user);

				if (!this.emailRegex.test(user['Userid'])) {
					user['Email'] = String(user['Userid'] + '@' + orgCode + '.ignite');
				} else {
					user['Email'] = user['Userid'];
				}
				user["Email"] = String(user["Email"]).toLowerCase().trim();
				const userEmail = user['Email'];
				let doesUserExist = await this.checkIfUserExists(user['Email']);

				if (user['Action'] === 'A') {
					if (!doesUserExist) {
						let userObjData: any = this.createUserObj(user, 'add', orgRoles);

						if (!userObjData.isValid) {
							const msg = `\nUser with email : ${user['Email']} has a role which does not exist!`;
							console.log(msg + '(A)');
							const newVal = `${this.bulkUploadDetails$.value} \n${msg}`;
							this.bulkUploadDetails$.next(newVal);
							unsuccessfulCount += 1;
						} else {
							const userValidityIndex = this.getUserValidityIndex(user, hierarchies);
							let userObj = userObjData.obj;
							// console.log(userValidityIndex);

							if (userValidityIndex.isValid) {
								// console.log(organisationHierarchy);
								organisationHierarchy = this.editOrgHierarchy(organisationHierarchy, hierarchies, user, userValidityIndex.index);
								userObj = this.addLevelsToUser(user, userObj);
								// console.log(userObj);
								// console.log(organisationHierarchy);
								await addDoc(collection(this.firestore, "tempUsers"), userObj);
								addedCount += 1;
							} else {
								console.log(userValidityIndex.reason + '(A)');
								const newVal = `${this.bulkUploadDetails$.value} \n${userValidityIndex.reason}`;
								this.bulkUploadDetails$.next(newVal);
								unsuccessfulCount += 1;
							}
						}
					} else {
						const msg = `\nUser with email : ${user['Email']} already exists!`;
						console.log(msg + '(A)');
						const newVal = `${this.bulkUploadDetails$.value} ${msg}`;
						this.bulkUploadDetails$.next(newVal);
						unsuccessfulCount += 1;
					}
				} else if (user['Action'] === 'U') {
					if (!doesUserExist) {
						const msg = `\nUser with email : ${user['Email']} does not exist.`;
						console.log(msg + '(U)');
						const newVal = `${this.bulkUploadDetails$.value} ${msg}`;
						this.bulkUploadDetails$.next(newVal);
						unsuccessfulCount += 1;
					} else {
						let userObjData = this.createUserObj(user, 'edit', orgRoles);

						if (!userObjData.isValid) {
							const msg = `\nUser with email : ${user['Email']} has a role which does not exist!`;
							console.log(msg + '(U)');
							const newVal = `${this.bulkUploadDetails$.value} ${msg}`;
							this.bulkUploadDetails$.next(newVal);
							unsuccessfulCount += 1;
						} else {
							const userValidityIndex = this.getUserValidityIndex(user, hierarchies);
							let userObj = userObjData.obj;
							// console.log(userValidityIndex);

							if (userValidityIndex.isValid) {
								organisationHierarchy = this.editOrgHierarchy(organisationHierarchy, hierarchies, user, userValidityIndex.index);
								userObj = this.addLevelsToUser(user, userObj);

									const userQuery = query(this.usersCollection, where('email', '==', userEmail));
									const userSnap = await getDocs(userQuery);
									const userId = userSnap.docs[0].id;

									const password = String(userObj.password);
									delete userObj.password;
									let userDocRef = doc(this.firestore, `users/${userId}`);
									await updateDoc(userDocRef, userObj);
									await this.passwordManagementService.adminSetPassword(userId, password, false);
									editedCount += 1;
							} else {
								console.log(userValidityIndex.reason + '(U)');
								const newVal = `${this.bulkUploadDetails$.value} \n${userValidityIndex.reason}`;
								this.bulkUploadDetails$.next(newVal);
								unsuccessfulCount += 1;
							}
						}
					}
				} else {
					const msg = `\nUser with email : ${user['Email']} has invalid action!`;
					console.log(msg);
					const newVal = `${this.bulkUploadDetails$.value} ${msg}`;
					this.bulkUploadDetails$.next(newVal);
					unsuccessfulCount += 1;
				}
			}

			await this.createOrganisationHierarchyService.mergeOrganisationHierarchy(organisationId, organisationHierarchy);
			let bulkUploadVal = this.bulkUploadDetails$.value;

			if (bulkUploadVal.includes("File is being imported...")) {
				bulkUploadVal = bulkUploadVal.replace("File is being imported...", "");
				this.bulkUploadDetails$.next(bulkUploadVal);
			}

			let msg = `${this.bulkUploadDetails$.value}\nExcel file processed successfully.\nRecords processed: Users Added - ${addedCount}, Updated - ${editedCount}, Failed - ${unsuccessfulCount}`;
			return {
				status: 1,
				msg
			};
		} else {
			this.bulkUploadDetails$.next(areRowsValid.reason ?? '');
			return {
				status: 0,
				msg: areRowsValid.reason
			};
		}
	}

	async updateOrganisationHierarchy(hierarchy: any, id: string) {
		// console.log('id',id);
		// console.log('data', hierarchy);
		try {
			const ref = doc(this.firestore, `organisations/${id}`);
			await updateDoc(ref, {
				hierarchy: hierarchy
			});
		} catch (err) {
			console.log('Could not create new templates: ', err);
		}
	}

	async checkIfUserExists(email: string) {
		// console.log(email);
		try {
			const userQuery = query(this.usersCollection, where("email", "==", email));
			const userSnap = await getDocs(userQuery);

			if (!userSnap.empty) {
				return true;
			}

			return false;
		} catch (err) {
			console.log('Unable to check if user exists', err);
		}
	}

	async getRoleName(roleId: string) {
		try {
			const roleDocRef = doc(this.rolesCollection, roleId);
			const roleDocSnap = await getDoc(roleDocRef);
			const roleData = roleDocSnap.data();
			return roleData && roleData['name'] ? roleData['name'] : '';
		} catch (err) {
			console.log('Unable to get role name.', err);
		}
	}

	async isRoleIdValid(roleId: string) {
		try {
			const roleDocRef = doc(this.rolesCollection, roleId);
			const roleDocSnap = await getDoc(roleDocRef);
			const roleData = roleDocSnap.data();

			if (roleData) {
				return true;
			}

			return false;
		} catch (err) {
			console.log('Unable to check if role id is valid.');
		}
	}

	async hashPassword(algorithm: string, password: string) {
		return await crypto.subtle.digest(algorithm, new TextEncoder().encode(password));
	}

	base64Encode (buff: any) {
		return btoa(new Uint8Array(buff).reduce((s, b) => s + String.fromCharCode(b), ''));
	}

	getRoleId(roleName: string, orgRoles: any) {
		for (let [key, val] of Object.entries(orgRoles)) {
			if (val === roleName) {
				return key;
			}
		}
	}

	async getGroupId(groupName: string, orgId: string) {
		try {
			let groupQuery = query(this.groupsCollection, where("name", "==", groupName), where("organisationId", "==", orgId));
			let groupSnap = await getDocs(groupQuery);

			if (!groupSnap.empty) {
				return groupSnap.docs[0].id;
			} else {
				groupName = groupName.charAt(0).toUpperCase() + groupName.slice(1);
				groupQuery = query(this.groupsCollection, where("name", "==", groupName), where("organisationId", "==", orgId));
				groupSnap = await getDocs(groupQuery);

				if (!groupSnap.empty) {
					return groupSnap.docs[0].id;
				}

				return '';
			}
		} catch (err) {
			console.log('Unable to get group id by name: ', err)
		}
	}

	async deleteUserProgress(userId: string) {
		try {
			const progressQuery = query(collection(this.firestore, "progress"), where("userId", "==", userId));
			const progressQuerySnap = await getDocs(progressQuery);
			if (progressQuerySnap.size === 0) {
				return {
					status: 'not-required'
				};
			}

			progressQuerySnap.forEach(async doc => {
				const docRef = doc.ref;
				await deleteDoc(docRef);
			});

			return {
				status: 'success'
			};
		} catch (err) {
			console.log(`Unable to delete progress for user ${userId}: `, err);
			return {
				status: 'failed'
			};
		}
	}

	checkIfExcelIsValid(fileData: any) {
		for (let row of fileData) {
			if (!row['Name'] || !row['Role'] || !row['Password'] || !row['Userid'] || !row['Action']) {
				let missingColumnName = "";
				if (!row['Name']) {
					missingColumnName = "Name";
				}
				if (!row['Role']) {
					if (missingColumnName.length >= 1) {
						missingColumnName += ", Name";
					} else {
						missingColumnName = "Name";
					}
				}
				if (!row['Password']) {
					if (missingColumnName.length >= 1) {
						missingColumnName += ", Password";
					} else {
						missingColumnName = "Password";
					}
				}
				if (!row['Userid']) {
					if (missingColumnName.length >= 1) {
						missingColumnName += ", Userid";
					} else {
						missingColumnName = "Userid";
					}
				}
				if (!row['Action']) {
					if (missingColumnName.length >= 1) {
						missingColumnName += ", Action";
					} else {
						missingColumnName = "Action";
					}
				}

				return {
					isValid: false,
					reason: `${missingColumnName} column was not found. Please fix the excel file.`
				}
			}
		}

		return {
			isValid: true
		};
	}

	createUserObj(user: any, action: string, orgRoles: any) {
		const organisationId = this.tabsService.organisationId$.getValue();
		const roleId = this.getRoleId(user['Role'], orgRoles);

		if (!roleId) {
			return {
				isValid: false,
				reason: 'This role does not exist in this organisation'
			};
		}

		let userObj: any = {};
		if (action === 'add') {
			userObj = {
				name: user['Name'],
				email: user['Email'],
				phone: String(user['Phone'] ? user['Phone'] : ""),
				password: String(user['Password']),
				// role: user['Role'],
				roleId,
				organisationId,
				userPic: "",
				level: [],
				createdAt: new Date()
			};

			if (!this.emailRegex.test(user['Userid'])) {
				userObj['empCode'] = String(user['Userid']).toLowerCase().trim();
			}
		} else if (action === 'edit') {
			userObj = {
				name: user['Name'],
				phone: String(user['Phone'] ? user['Phone'] : ""),
				roleId,
				password: String(user['Password']),
				level: [],
				updatedAt: new Date()
			};
		}

		return {
			obj: userObj,
			isValid: true
		};
	}

	addLevelsToUser(user: any, userObj: any) {
		for (let userKey of Object.keys(user)) {
			if (userKey !== 'Name' && userKey !== 'Email' && userKey !== 'Password' && userKey !== 'Phone' && userKey !== 'Role' && userKey !== '0' && userKey !== 'Userid' && userKey !== 'empCode') {
				userObj.level.push({
					name: userKey.toLowerCase(),
					value: String(user[userKey]).toLowerCase().trim()
				});
			}
		}

		// console.log(userObj);
		return userObj;
	}

	getUserValidityIndex(user: any, hierarchies: any) {
		let index = -1;
		let predefinedKeys = ["Name", "Userid", "Action", "Password", "Phone", "Role", "0", "Email"];
		let validKeys = [...predefinedKeys, ...hierarchies];
		// console.log(validKeys);

		for (let userKey of Object.keys(user)) {
			let orgValue = user[userKey];

			let newUserKey;
			if (!predefinedKeys.includes(userKey)) {
				newUserKey = String(userKey.toLowerCase()).trim();
			} else {
				newUserKey = String(userKey).trim();
			}

			user[newUserKey] = orgValue;
			delete user[userKey];
			if (!validKeys.includes(newUserKey) && !validKeys.includes(newUserKey.toLowerCase())) {
				// console.log(userKey);
				return {
					isValid: false,
					reason: `${user['Email']} contains ${newUserKey} key, which is invalid.`
				};
			}
		}

		for (let hierarchy of hierarchies) {
			if (!user[hierarchy]) {
				break;
			}
			index += 1;
		}

		if (index === -1) {
			return {
				isValid: false,
				reason: `${user['Email']} does not have the correct organization hierarchy.`
			};
		}

			const passwordLength = String(user['Password']).length;
			if (passwordLength < 8 || passwordLength > 128) {
				return {
					isValid: false,
					reason: `${user['Email']} should have a password between 8 and 128 characters.`
				};
			}

		return {
			isValid: true,
			index
		};
	}

	editOrgHierarchy(organisationHierarchy: any, hierarchies: any, user: any, index: any) {
		for (let currentLevelIndex = 0; currentLevelIndex <= index; currentLevelIndex += 1) {
			const levelName = hierarchies[currentLevelIndex];
			const newDisplayValue = String(user[levelName]).trim();
			const newValue = String(newDisplayValue).toLowerCase().trim();
			// console.log(newValue);
			let prevLevelsInHierarchy = [...hierarchies.slice(0, currentLevelIndex)];
			// console.log(currentLevelIndex)
			// console.log(prevLevelsInHierarchy);
			let prevLevelsInUser: any = [];
			for (let level of prevLevelsInHierarchy) {
				prevLevelsInUser.push(String(user[level]).toLowerCase().trim());
			}
			// console.log(prevLevelsInUser);

			if (!organisationHierarchy[currentLevelIndex]) {
				organisationHierarchy[currentLevelIndex] = {
					values: []
				};
			}

			if (organisationHierarchy[currentLevelIndex] && Object.keys(organisationHierarchy[currentLevelIndex]).length >= 1 && organisationHierarchy[currentLevelIndex].values.length >= 1) {
				let levelAlreadyExists = true;
				const vals = organisationHierarchy[currentLevelIndex].values;
				// console.log(vals);

				for (let val of vals) {
					// console.log(val);
					for (var i = 0; i < val.levels.length; i += 1) {
						// console.log(val.levels[i]);
						// console.log(prevLevelsInUser[i]);
						if (val.levels[i] !== prevLevelsInUser[i]) {
							// console.log('Level no match');
							// console.log(val.levels);
							levelAlreadyExists = false;
							break;
						}
					}

					if (i === val.levels.length) {
						levelAlreadyExists = true;
					}
					// console.log(levelAlreadyExists)

					if (levelAlreadyExists) {
						// console.log(val.value);
						// console.log(newValue);
						if (val.value === newValue) {
							break;
						} else {
							levelAlreadyExists = false;
						}
					}
				}

				if (!levelAlreadyExists) {
					organisationHierarchy[currentLevelIndex].values.push({
						levels: [...prevLevelsInUser],
						value: newValue,
						displayValue: newDisplayValue
					});
				}
			} else {
				organisationHierarchy[currentLevelIndex] = {
					name: levelName,
					values: [{
						levels: [...prevLevelsInUser],
						value: newValue,
						displayValue: newDisplayValue
					}]
				}
			}
		}

		return organisationHierarchy;
	}

	getTrimmedUser(user: any) {
		for (let key of Object.keys(user)) {
			if (key.length !== key.trim().length) {
				// console.log(key);
				user[key.trim()] = user[key];
				delete user[key];
			}
		}

		return user;
	}

	async getAllUsersInOrg(orgId: string) {
		try {
			let userIds: any = [];
			const userQuery = query(this.usersCollection, where('organisationId', '==', orgId));
			const userQuerySnap = await getDocs(userQuery);

			for (let user of userQuerySnap.docs) {
				userIds.push(user.id);
			}

			return userIds;
		} catch (err) {
			console.log('Could not get all users', err);
		}
	}

	async getAllUsers(orgId: string) {
		let users: any = [];
		const userQuery = query(this.usersCollection, where('organisationId', '==', orgId));
		const userQuerySnap = await getDocs(userQuery);

		for (let user of userQuerySnap.docs) {
			const userData = user.data();
			let userObj: any = {
				id: user.id
			};

			for (let key of Object.keys(userData)) {
				userObj[key] = userData[key];
			}

			users.push(userObj);
		}

		return users;
	}

	async getAllUserIdsAllOrgs() {
		let users: any = [];
		const userQuery = query(this.usersCollection);
		const userQuerySnap = await getDocs(userQuery);

		for (let user of userQuerySnap.docs) {
			users.push(user.id);
		}

		return users;
	}

	getAllUsersObs(orgId: string) {
		const userQuery = query(this.usersCollection, where('organisationId', '==', orgId));
		const userDataObs = collectionData(userQuery, {
			idField: 'id'
		});
		return userDataObs;
	}

	async getOrgRoles(orgId: string) {
		try {
			let orgRoles: any = {};
			const rolesQuery = query(collection(this.firestore, 'roles'), where('organisationId', '==', orgId));
			const rolesObs = collectionData(rolesQuery, {
				idField: "id"
			});

			const rolesData: any = await this.sharedService.getObservableRes(rolesObs);
			// console.log(rolesData);
			rolesData.forEach((role: any) => {
				orgRoles[String(role.id)] = role.name;
			});

			return orgRoles;
		} catch (err) {
			console.log(err);
		}
	}

	async getUserDetails(userId: string) {
		try {
			const userObs = docData(doc(this.firestore, `users/${userId}`));
			return await this.sharedService.getObservableRes(userObs);
		} catch (err) {
			console.log('Unable to get user details', err);
		}
	}

	async checkIfUserExistsByCode(empCode: string) {
		try {
			const userQuery = query(this.usersCollection, where("empCode", "==", empCode));
			const userSnap = await getDocs(userQuery);

			if (!userSnap.empty) {
				return true;
			}

			return false;
		} catch (err) {
			console.log('Unable to check if user exists by code');
		}
	}

	async checkIfUserExistsByCodeInOrg(empCode: string, orgId: string) {
		try {
			const userQuery = query(this.usersCollection, where("empCode", "==", empCode), where("organisationId", "==", orgId));
			const userSnap = await getDocs(userQuery);

			if (!userSnap.empty) {
				return true;
			}

			return false;
		} catch (err) {
			console.log('Unable to check if user exists by code');
		}
	}

	async getUserEnrollmentIds(userId: string) {
		try {
			const progressQuery = query(this.progressCollection, where("userId", "==", userId));
			const progressSnap = await getDocs(progressQuery);

			let programIds: any = [];
			if (progressSnap.empty) {
				return programIds;
			}

			progressSnap.forEach((progress: any) => {
				programIds.push(progress.data());
			});

			return programIds;
		} catch (err) {
			console.log('Unable to get enrollments for user id', userId);
		}
	}

	async getMoreUsers(orgId: any, lastUserDoc: any) {
		const q = query(this.usersCollection, where('organisationId', '==', orgId), orderBy('createdAt', 'desc'), limit(100), startAfter(lastUserDoc));
		const snapshot = await getDocs(q);

		let users = [];
		if (!snapshot.docs?.length) {
			return {
				status: 'no_more_leads', users: []
			}
		};

		let newLastUserDoc = snapshot.docs[snapshot.docs.length - 1];
		for (const doc of snapshot.docs) {
			users.push({ ...doc.data(), id: doc.id });
		}
		return {
			status: 'available', users, lastUserDoc: newLastUserDoc
		};
	}

	async getPreviousUsers(orgId: any, lastDoc: any) {
		if (!lastDoc) {
			return {
				status: 'no_more_leads',
				users: [],
			};
		}

		const q = query(
			this.usersCollection,
			where('organisationId', '==', orgId),
			orderBy('createdAt', 'desc'),
			startAfter(lastDoc),
			limit(100)
		);

		const snapshot = await getDocs(q);

		if (!snapshot.docs?.length) {
			return {
				status: 'no_more_leads',
				users: [],
			};
		}

		let users = [];
		for (const doc of snapshot.docs) {
			users.push({ ...doc.data(), id: doc.id });
		}

		return {
			status: 'available',
			users,
			lastUserDoc: snapshot.docs[snapshot.docs.length - 1], // Update the last document
		};
	}

	fetchLatestUsers(orgId: any): Observable<any[]> {
		// Define your query to retrieve the latest 20 users
		const q = query(
			this.usersCollection,
			where('organisationId', '==', orgId),
			orderBy('createdAt', 'desc'),
			limit(20)
		);

		return new Observable(observer => {
			getDocs(q)
				.then(snapshot => {
					const latestUsers = [];
					for (const doc of snapshot.docs) {
						const user = { ...doc.data(), id: doc.id };
						latestUsers.push(user);
					}
					// Check for duplicates and add only new users
					// const existingUsers = this.latestUsers$.getValue();
					// for (const user of latestUsers) {
					// 	if (!existingUsers.find((u: any) => u.id === user.id)) {
					// 		existingUsers.unshift(user);
					// 	}
					// }
					// // Limit the array to 20 users
					// if (existingUsers.length > 20) {
					// 	existingUsers.length = 20;
					// }
					// this.latestUsers$.next(existingUsers);
					// observer.next(existingUsers);
					observer.complete();
				})
				.catch(error => {
					observer.error(error);
				});
		});
	}

	async getInitialUsers(orgId: string) {
		const q = query(this.usersCollection, where('organisationId', '==', orgId), orderBy('createdAt', 'desc'), limit(100));
		const snapshot = await getDocs(q);
		if (!snapshot.docs?.length) {
			return {
				status: 'no_leads', users: []
			}
		}
		let users = [];
		let lastUserDoc = snapshot.docs[snapshot.docs.length - 1];
		for (const doc of snapshot.docs) {
			users.push({ ...doc.data(), id: doc.id });
		}
		return {
			status: 'available', users, lastUserDoc
		};
	}

	async fetchMgrDetails(ids: any) {
		// Managers are read straight from Firestore rather than Typesense.
		// The search indexes are eventually-consistent and one of them ("ignite-users-prod")
		// is a legacy index the backend no longer syncs, which meant deleted users kept
		// re-appearing in the managers list. Firestore is the source of truth.
		if (!ids || ids.length < 1) {
			return [];
		}

		// Drop null/duplicate ids - historic data contains both.
		const uniqueIds: string[] = Array.from(new Set(
			ids.filter((id: any) => typeof id === 'string' && id.length >= 1)
		));

		if (uniqueIds.length < 1) {
			return [];
		}

		const users: any = [];
		// Firestore allows at most 30 values in a documentId() "in" query.
		const chunkSize = 30;

		try {
			for (let i = 0; i < uniqueIds.length; i += chunkSize) {
				const chunk = uniqueIds.slice(i, i + chunkSize);
				const mgrQuery = query(this.usersCollection, where(documentId(), 'in', chunk));
				const snapshot = await getDocs(mgrQuery);

				snapshot.forEach((mgrDoc) => {
					users.push({ ...mgrDoc.data(), id: mgrDoc.id });
				});
			}
		} catch (err) {
			console.log('Unable to fetch manager details: ', err);
			throw err;
		}

		return users;
	}

	async searchUsersTypesense(searchValue: string, orgId: string) {
		return new Promise((resolve, reject) => {
			let res: any = [];

			let searchParameters = {
				'q': searchValue,
				'query_by': 'name, empCode, email',
				'filter_by': `organisationId:${orgId}`,
				'per_page': 250
			};

			const fallbackSearchParameters = {
				'q': searchValue,
				'query_by': 'name, empCode',
				'filter_by': `organisationId:${orgId}`,
				'per_page': 250
			};

			const trySearch = async () => {
				let lastError: any = null;

				for (const collectionName of this.usersSearchCollections) {
					try {
						const response = await this.typesenseClient.collections(collectionName).documents().search(searchParameters)
							.catch(async () => {
								// Fallback for indexes where "email" may not be part of query_by schema yet.
								return await this.typesenseClient.collections(collectionName).documents().search(fallbackSearchParameters);
							});

						if (response?.hits?.length) {
							response.hits.forEach((h: any) => {
								h.document = this.parseArrayOfObjects(h.document);
								res.push({ ...h.document, id: h.document.id });
							});
						}

						// Stop at first valid collection response (even if 0 hits).
						resolve(res);
						return;
					} catch (err: any) {
						lastError = err;
					}
				}

				reject(lastError ?? new Error('Unable to search users from Typesense.'));
			};

			trySearch();
		});
	}

	async doesAnyUserExistAtLevel(levels: String, orgId: String) {
		return new Promise((resolve, reject) => {
			try {
				let searchParameters = {
					'q': levels,
					'query_by': 'level',
				};

				this.typesenseClient.collections(this.usersSearchCollections[0]).documents().search(searchParameters).then((response: any) => {
					// for (let user of orgAllUsers) {
					// 	let atLevel = true;

					// 	for (let i = 0; i < levels.length; i += 1) {
					// 		if (!user.level[i] || levels[i] !== user.level[i].value) {
					// 			atLevel = false;
					// 			break;
					// 		}
					// 	}

					// 	if (atLevel) {
					// 		return true;
					// 	}
					// }

					// return false;

					// console.log(response);
					if (response && response.hits && response.hits.length >= 1) {
						response.hits.forEach((hit: any) => {
							if (hit.document.organisationId === orgId && hit.document.level.includes(levels)) {
								resolve(true);
							}
						});
					}
					resolve(false);
				});

			} catch (err) {
				console.log(err);
			}
		});
	}

	initTypesenseClient() {
		this.typesenseClient = new Typesense.SearchClient({
			'nodes': [{
				'host': this.typesenseCred.host,
				'port': this.typesenseCred.port,
				'protocol': this.typesenseCred.protocol
			}],
			'apiKey': this.typesenseCred.searchOnlyKey,
			'connectionTimeoutSeconds': 2
		});
	}

	parseArrayOfObjects(object: any) {
		for (const key in object) {
			try {
				object[key] = JSON.parse(object[key]);
			} catch (error) {
				// console.log(error);
			}
		}
		return object;
	}
}
